import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLoading } from "@/providers/loading-provider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Link, useLocation, useParams } from "wouter";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

interface Transaction {
  id: number;
  businessId: number;
  date: string;
  description: string;
  amount: string;
  type: string;
  status: string;
  categoryId: number | null;
  metadata: {
    paymentMethod?: string;
    vendor?: string;
    notes?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface TransactionCategory {
  id: number;
  name: string;
  description: string;
  color: string;
}

export default function EditExpensePage() {
  const { id } = useParams();
  const [_, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentBusiness } = useCurrentBusiness();
  const { stopLoading } = useLoading();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    categoryId: "",
    paymentMethod: "credit_card",
    vendor: "",
    notes: ""
  });

  // Query to fetch the transaction to edit
  const { data: transaction, isLoading, error } = useQuery<Transaction>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/transactions/${id}`],
    enabled: !!currentBusiness && !!id,
  });

  // Query to fetch transaction categories
  const categoriesUrl = `/api/transaction-categories`;
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery<TransactionCategory[]>({
    queryKey: [categoriesUrl],
    enabled: !!currentBusiness?.id,
  });

  // Query to fetch vendors with proper query params
  const vendorsUrl = currentBusiness?.id ? `/api/businesses/${currentBusiness.id}/vendors` : '';
  const { data: vendorsData, isLoading: isLoadingVendors } = useQuery<Array<{ id: number; name: string }>>({
    queryKey: [vendorsUrl],
    enabled: !!currentBusiness?.id && vendorsUrl !== '',
  });

  // Populate form data when transaction is loaded
  useEffect(() => {
    if (transaction) {
      setFormData({
        description: transaction.description || "",
        amount: transaction.amount || "",
        categoryId: transaction.categoryId ? transaction.categoryId.toString() : "",
        paymentMethod: transaction.metadata?.paymentMethod || "credit_card",
        vendor: transaction.metadata?.vendor || "",
        notes: transaction.metadata?.notes || ""
      });
      if (transaction.date) {
        setDate(new Date(transaction.date));
      }
      // Stop the loading spinner once data is loaded
      stopLoading();
    }
  }, [transaction, stopLoading]);

  // Mutation to update the transaction
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("PATCH", `/api/businesses/${currentBusiness?.id}/transactions/${id}`, data);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Expense updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/transactions`] });
      setLocation("/expenses/expenses");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update expense: ${error.message}`,
        variant: "destructive",
      });
    }
  });

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle select changes
  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentBusiness) {
      toast({
        title: "Error",
        description: "No business selected",
        variant: "destructive",
      });
      return;
    }

    if (!date) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    // Convert categoryId to a number
    const categoryId = formData.categoryId ? parseInt(formData.categoryId) : null;

    // Prepare the data for the API request
    const transactionData = {
      date: date.toISOString(),
      description: formData.description,
      amount: formData.amount,
      categoryId,
      metadata: {
        paymentMethod: formData.paymentMethod,
        vendor: formData.vendor,
        notes: formData.notes,
      },
    };

    updateMutation.mutate(transactionData);
  };

  if (!currentBusiness) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">No Business Selected</h2>
            <p className="text-gray-600 mb-4">Please select a business to continue.</p>
            <Button asChild>
              <Link href="/">Go to Dashboard</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading expense data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-2">Error</h2>
            <p className="text-gray-600 mb-4">Failed to load the expense data.</p>
            <Button asChild>
              <Link href="/expenses/expenses">Back to Expenses</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            asChild
            className="mr-4"
          >
            <Link href="/expenses/expenses">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Expenses
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Expense</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Expense Details</CardTitle>
            <CardDescription>Make changes to the expense information below.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                      )}
                    >
                      {date ? format(date, "PPP") : "Select a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(date) => {
                        setDate(date);
                        setIsCalendarOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter expense description"
                  required
                />
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="categoryId">Category</Label>
                <Select
                  value={formData.categoryId}
                  onValueChange={(value) => handleSelectChange("categoryId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingCategories ? (
                      <div className="flex items-center justify-center p-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading...</span>
                      </div>
                    ) : categoriesData && Array.isArray(categoriesData) ? (
                      categoriesData.map((category: any) => (
                        <SelectItem key={category.id} value={String(category.id)}>
                          {category.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-category">No categories available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label htmlFor="paymentMethod">Payment Method</Label>
                <Select
                  value={formData.paymentMethod}
                  onValueChange={(value) => handleSelectChange("paymentMethod", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="debit_card">Debit Card</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="wire_transfer">Wire Transfer</SelectItem>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Vendor */}
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Select
                  value={formData.vendor}
                  onValueChange={(value) => handleSelectChange("vendor", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select or enter vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingVendors ? (
                      <div className="flex items-center justify-center p-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading...</span>
                      </div>
                    ) : vendorsData && Array.isArray(vendorsData) ? (
                      vendorsData.map((vendor: any) => (
                        <SelectItem key={vendor.id} value={vendor.name}>
                          {vendor.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-vendors">No vendors available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Add any additional notes"
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button type="button" variant="outline" asChild>
                  <Link href="/expenses/expenses">Cancel</Link>
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
